import { motion } from "framer-motion";
import { MessageCircle, Search, BadgeCheck, ArrowUpRight } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const steps = [
  {
    id: "send",
    num: "01",
    icon: MessageCircle,
    title: "Rechnung senden",
    desc: "Öffne WhatsApp mit einem Klick und schick uns ein Foto oder PDF deiner aktuellen Strom- oder Gasrechnung. Dauert keine 60 Sekunden.",
    accent: "WhatsApp",
  },
  {
    id: "analyze",
    num: "02",
    icon: Search,
    title: "Wir analysieren",
    desc: "Unsere Berater prüfen manuell über 1.000 Tarife und finden das beste Angebot für deinen Verbrauch und deine Postleitzahl.",
    accent: "Persönlich",
  },
  {
    id: "offer",
    num: "03",
    icon: BadgeCheck,
    title: "Angebot in 24h",
    desc: "Du erhältst dein individuelles Angebot direkt im Chat – unverbindlich. Sagt dir der Tarif zu, übernehmen wir den kompletten Wechsel.",
    accent: "24h",
  },
];

export default function AblaufSection() {
  return (
    <section
      id="ablauf"
      className="relative py-20 sm:py-28 bg-brand-surface overflow-hidden"
      data-testid="ablauf-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="text-[11px] font-bold tracking-[0.25em] text-brand-energy uppercase">So funktioniert's</p>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-brand-ink tracking-[-0.035em] leading-[0.95]">
              In 3 Schritten<br />zum neuen Tarif.
            </h2>
          </div>
          <p className="text-base text-brand-muted max-w-sm md:text-right leading-relaxed">
            Kein Formular, keine Anmeldung. Du chattest direkt mit uns – so einfach wie eine Nachricht an Freunde.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {steps.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease, delay: i * 0.08 }}
              className={`group relative rounded-3xl p-7 md:p-8 border transition-all duration-500 ease-out ${
                i === 1
                  ? "bg-brand-inverse text-brand-onInverse border-brand-inverse hover:bg-black"
                  : "bg-brand-paper text-brand-ink border-brand-line hover:border-brand-ink hover:bg-brand-surface"
              }`}
              data-testid={`step-${s.id}`}
            >
              <span
                aria-hidden
                className={`absolute top-4 right-5 font-heading text-[5.5rem] font-black leading-none tracking-tighter select-none ${
                  i === 1 ? "text-white/10" : "text-brand-ink/[0.06]"
                }`}
              >
                {s.num}
              </span>

              <div className="relative w-12 h-12 rounded-2xl flex items-center justify-center mb-8 transition-transform duration-500 group-hover:scale-110 bg-brand-energy">
                <s.icon className="w-5 h-5" style={{ color: '#0A0A0A' }} strokeWidth={2.4} />
              </div>

              <p className={`text-[10px] font-bold uppercase tracking-[0.25em] mb-2`} style={{ color: i === 1 ? 'var(--brand-green-neon)' : 'var(--brand-green)' }}>
                {s.accent}
              </p>
              <h3 className="font-heading text-2xl md:text-[1.65rem] font-black tracking-tight leading-tight">
                {s.title}
              </h3>
              <p className={`mt-3 text-sm leading-relaxed ${i === 1 ? "text-neutral-300" : "text-brand-muted"}`}>
                {s.desc}
              </p>

              {i < steps.length - 1 && (
                <div className="hidden md:flex absolute top-1/2 -right-[14px] -translate-y-1/2 w-7 h-7 rounded-full bg-brand-surface border border-brand-line items-center justify-center z-10">
                  <ArrowUpRight className="w-3.5 h-3.5 rotate-45 text-brand-ink" strokeWidth={2.5} />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
